'use client';

import type { DragEndEvent } from '@dnd-kit/core';
import type {
  ColumnDef,
  ColumnOrderState,
  ColumnSizingState,
  FilterFn,
  OnChangeFn,
  RowData,
  RowSelectionState,
  VisibilityState,
} from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Checkbox } from '@savvy-kit/components/checkbox';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHeader,
  TableRow,
} from '@savvy-kit/components/table';
import { cn } from '@savvy-kit/lib/utils';
import { Pagination } from '@savvy-kit/patterns/pagination';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { isNil } from 'es-toolkit';

import type { DataTableProps } from '../types/data-table-props';
import { useColumnVisibility } from '../hooks/use-column-visibility';
import { useDataTableFilters } from '../hooks/use-data-table-filters';
import { useExport } from '../hooks/use-export';
import { getColumnOrderStorageKey, getStoredColumnOrder, setStoredColumnOrder } from '../utils/column-order-storage';
import {
  getDefaultColumnOrder,
  isColumnOrderable,
  reconcileColumnOrder,
  reorderColumnsWithinSubset,
} from '../utils/column-ordering';
import {
  getColumnVisibilityStorageKey,
  getStoredColumnVisibility,
  setStoredColumnVisibility,
} from '../utils/column-visibility-storage';
import {
  checkboxFilterFn,
  dateFilterFn,
  datetimeFilterFn,
  numberFilterFn,
  selectFilterFn,
  textFilterFn,
  timeFilterFn,
} from '../utils/filter-fns';
import { getActiveFilters } from '../utils/get-active-filters';
import { getFilterableColumns } from '../utils/get-filterable-columns';
import { normalizeExportConfig } from '../utils/normalize-export-config';
import { resolveClassName } from '../utils/resolve-class-name';
import { resolveSlotProps } from '../utils/resolve-slot-props';
import { DataTableContext } from './data-table-context';
import { DataTablePageSize } from './data-table-page-size';
import { DataTableToolbarBottom } from './data-table-toolbar-bottom';
import {
  DefaultCell,
  DefaultFooterCell,
  DefaultFooterGroup,
  DefaultHeaderCell,
  DefaultHeaderGroup,
  DefaultRow,
} from './default-slots';
import { DataTableExportButton } from './export/export-button';
import { DataTableExportModal } from './export/export-modal';
import { BULK_SELECT_ROW_GROUP, BulkSelectReveal } from './selection/bulk-select-reveal';
import { SelectionBar } from './selection/selection-bar';
import { DataTableActiveFilters } from './toolbar/data-table-active-filters';
import { DataTableColumnVisibility } from './toolbar/data-table-column-visibility';
import { DataTableFilters } from './toolbar/data-table-filters';
import { DataTableToolbarTop } from './toolbar/data-table-toolbar-top';

const filterFns: Record<string, FilterFn<unknown>> = {
  select: selectFilterFn,
  tag: selectFilterFn,
  text: textFilterFn,
  number: numberFilterFn,
  currency: numberFilterFn,
  date: dateFilterFn,
  time: timeFilterFn,
  datetime: datetimeFilterFn,
  checkbox: checkboxFilterFn,
};

function toCssLength(value: number | string) {
  return typeof value === 'number' ? `${value}px` : value;
}

function getDefaultColumnVisibility<TData extends RowData>(columns: DataTableProps<TData>['columns']) {
  const initial: VisibilityState = {};
  for (const col of columns) {
    if (col.meta?.columnVisibility?.defaultVisible !== false) continue;
    // Resolve ID: explicit id > accessorKey (TanStack's implicit ID for accessor columns)
    const colId = col.id ?? ('accessorKey' in col ? String((col as { accessorKey: unknown }).accessorKey) : undefined);
    if (!isNil(colId)) {
      initial[colId] = false;
    }
  }
  return initial;
}

export function DataTable<TData extends RowData>({
  name,
  columns,
  data,
  onRowChange,
  features: {
    header = true,
    footer = false,
    filtering = false,
    pagination = false,
    export: exportFeature = false,
    sorting = false,
    columnVisibility: columnVisibilityFeature = false,
    columnResizing = false,
    columnOrdering: columnOrderingFeature = false,
    bulkActions: bulkActionsConfig,
    virtualizeRows: virtualizeRowsFeature = false,
    stickyHeader: stickyHeaderFeature = false,
  } = {},
  getSubRows,
  defaultExpanded = true,
  columnVisibility,
  tableRef,
  classNames,
  slotProps,
  slots,
  size,
  borderless,
  caption,
  minTableWidth,
  emptyState,
  columnFiltersState,
  onColumnFiltersChange,
  paginationState,
  onPaginationChange,
  columnOrder,
  onColumnOrderChange,
}: DataTableProps<TData>) {
  const filteringEnabled = typeof filtering === 'boolean' ? filtering : true;
  const filteringConfig = typeof filtering === 'object' ? filtering : undefined;
  const isManualFiltering = filteringConfig?.manual ?? false;

  const stickyHeaderEnabled = stickyHeaderFeature !== false;
  const stickyHeaderConfig = typeof stickyHeaderFeature === 'object' ? stickyHeaderFeature : {};
  const stickyHeaderMaxHeight = stickyHeaderConfig.maxHeight;
  // `maxHeight` is what distinguishes the two modes: with it the table owns a scroll
  // region and the header pins inside it; without it the header pins against the page.
  const stickyHeaderScrollsInternally = !isNil(stickyHeaderMaxHeight);
  // `overflow: clip` is the load-bearing detail of page mode. It clips exactly like
  // `hidden` — so the card keeps its rounded corners over the header's background —
  // but it is not a scroll container, so a sticky `<thead>` still resolves against the
  // page rather than being captured here. `hidden` would capture it and never pin.
  const tableContentOverflowClass = (() => {
    if (!stickyHeaderEnabled) return 'overflow-hidden';
    return stickyHeaderScrollsInternally ? 'overflow-auto' : 'overflow-clip';
  })();
  const tableContentStyle = isNil(stickyHeaderMaxHeight)
    ? undefined
    : { maxHeight: toCssLength(stickyHeaderMaxHeight) };
  // Which mode is active is otherwise only visible in Tailwind classes, which makes it
  // untestable (the test config runs with `css: false`) and invisible in devtools.
  const stickyHeaderMode = (() => {
    if (!stickyHeaderEnabled) return undefined;
    return stickyHeaderScrollsInternally ? 'container' : 'page';
  })();

  const paginationEnabled = typeof pagination === 'boolean' ? pagination : true;
  const paginationConfig = typeof pagination === 'object' ? pagination : undefined;
  const isManualPagination = (typeof pagination === 'object' && pagination.manual) ?? false;

  const exportConfig = normalizeExportConfig(exportFeature);
  const columnVisibilityConfig = typeof columnVisibilityFeature === 'boolean' ? undefined : columnVisibilityFeature;
  const columnVisibilityEnabled = typeof columnVisibilityFeature === 'boolean' ? columnVisibilityFeature : true;
  const columnVisibilityStorageKey = getColumnVisibilityStorageKey({
    tableName: name,
    storageKey: columnVisibilityConfig?.storageKey,
    persist: columnVisibilityConfig?.persist ?? true,
  });
  const columnOrderingConfig = typeof columnOrderingFeature === 'boolean' ? undefined : columnOrderingFeature;
  const columnOrderingEnabled = typeof columnOrderingFeature === 'boolean' ? columnOrderingFeature : true;
  const columnOrderStorageKey = getColumnOrderStorageKey({
    tableName: name,
    storageKey: columnOrderingConfig?.storageKey,
    persist: columnOrderingConfig?.persist ?? true,
  });

  const bulkActionsEnabled = bulkActionsConfig !== undefined;
  const isRowSelectableFn = bulkActionsConfig?.isRowSelectable;
  const rowClickTogglesEnabled = bulkActionsConfig?.rowClickToggles ?? false;

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const selectedIds = useMemo(
    () => (bulkActionsEnabled ? Object.keys(rowSelection).filter((id) => rowSelection[id]) : []),
    [bulkActionsEnabled, rowSelection],
  );

  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  // Drop stale IDs when `data` changes (filter changes or post-mutation refetch).
  useEffect(() => {
    if (!bulkActionsEnabled) return;
    const { getRowId } = bulkActionsConfig;
    setRowSelection((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const currentIds = new Set(data.map((row) => getRowId(row)));
      const hasStale = Object.keys(prev).some((id) => !currentIds.has(id));
      if (!hasStale) return prev;
      return Object.fromEntries(Object.entries(prev).filter(([id]) => currentIds.has(id)));
    });
  }, [data, bulkActionsEnabled]);

  const checkRowSelectable = useCallback(
    (row: TData): boolean => {
      if (!isRowSelectableFn) return true;
      return isRowSelectableFn(row, selectedIdsRef.current);
    },
    [isRowSelectableFn],
  );

  const selectColumn = useMemo((): ColumnDef<TData> | null => {
    if (!bulkActionsEnabled) return null;
    const { getRowId } = bulkActionsConfig;
    const selectAllId = name !== undefined ? `${name}-select-all` : 'data-table-select-all';
    return {
      id: 'select',
      size: 48,
      enableSorting: false,
      meta: { columnOrdering: { enabled: false } },
      header: ({ table: t }) => {
        if (isRowSelectableFn) {
          const rows = t.getRowModel().rows;
          const ids = selectedIdsRef.current;
          const selectableRows = rows.filter((r) => isRowSelectableFn(r.original, ids));
          const allChecked = selectableRows.length > 0 && selectableRows.every((r) => r.getIsSelected());
          const someSelected = rows.some((r) => r.getIsSelected());
          let checked: boolean | 'indeterminate' = false;
          if (allChecked) checked = true;
          else if (someSelected) checked = 'indeterminate';
          return (
            <BulkSelectReveal>
              <Checkbox
                id={selectAllId}
                aria-label="Select all rows"
                checked={checked}
                disabled={selectableRows.length === 0}
                onCheckedChange={(v) => {
                  if (v === true) {
                    const next: RowSelectionState = {};
                    for (const r of selectableRows) next[r.id] = true;
                    setRowSelection(next);
                  } else {
                    setRowSelection({});
                  }
                }}
              />
            </BulkSelectReveal>
          );
        }
        const allSelected = t.getIsAllRowsSelected();
        const someSelected = t.getIsSomeRowsSelected();
        let headerChecked: boolean | 'indeterminate' = false;
        if (allSelected) headerChecked = true;
        else if (someSelected) headerChecked = 'indeterminate';
        return (
          <BulkSelectReveal>
            <Checkbox
              id={selectAllId}
              aria-label="Select all rows"
              checked={headerChecked}
              onCheckedChange={(checked) => t.toggleAllRowsSelected(checked === true)}
            />
          </BulkSelectReveal>
        );
      },
      cell: ({ row }) => {
        const isSelected = row.getIsSelected();
        const selectable = checkRowSelectable(row.original);
        return (
          <BulkSelectReveal
            visible={isSelected}
            data-bulk-select-cell
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
          >
            <Checkbox
              id={`${selectAllId}-${getRowId(row.original)}`}
              aria-label="Select row"
              checked={isSelected}
              disabled={!isSelected && !selectable}
              onCheckedChange={(checked) => {
                if (checked === true && !selectable) return;
                row.toggleSelected(checked === true);
              }}
            />
          </BulkSelectReveal>
        );
      },
    };
  }, [bulkActionsEnabled, name, isRowSelectableFn, checkRowSelectable]);

  const columnsWithSelection = useMemo(
    () => (selectColumn !== null ? [selectColumn, ...columns] : columns),
    [selectColumn, columns],
  );

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const measuredMinSizesRef = useRef<Record<string, number>>({});
  const [managedColumnOrder, setManagedColumnOrder] = useState<ColumnOrderState>(() => {
    if (!columnOrderingEnabled) return [];

    if (!isNil(columnOrder)) {
      return reconcileColumnOrder(columnOrder, columns);
    }

    const defaultColumnOrder = getDefaultColumnOrder(columns);
    if (isNil(columnOrderStorageKey)) return defaultColumnOrder;

    const storedColumnOrder = getStoredColumnOrder(columnOrderStorageKey);
    if (isNil(storedColumnOrder)) return defaultColumnOrder;

    return reconcileColumnOrder(storedColumnOrder, columns);
  });

  const setMinColumnSize = useCallback((columnId: string, minSize: number) => {
    measuredMinSizesRef.current[columnId] = minSize;
  }, []);

  const handleColumnSizingChange = useCallback<OnChangeFn<ColumnSizingState>>((updaterOrValue) => {
    setColumnSizing((prev) => {
      const next = typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue;
      return Object.fromEntries(
        Object.entries(next).map(([colId, size]) => [colId, Math.max(size, measuredMinSizesRef.current[colId] ?? 0)]),
      ) as ColumnSizingState;
    });
  }, []);

  const resizeColumn = useCallback(
    (columnId: string, newSize: number) => {
      handleColumnSizingChange((prev) => ({ ...prev, [columnId]: newSize }));
    },
    [handleColumnSizingChange],
  );

  // When a controlled columnOrder prop is provided, keep internal state in sync with it.
  useEffect(() => {
    if (columnOrderingEnabled && !isNil(columnOrder)) {
      setManagedColumnOrder(reconcileColumnOrder(columnOrder, columns));
    }
  }, [columnOrder, columnOrderingEnabled, columns]);

  useEffect(() => {
    if (!columnOrderingEnabled || !isNil(columnOrder) || isNil(columnOrderStorageKey)) {
      return;
    }

    const storedColumnOrder = getStoredColumnOrder(columnOrderStorageKey);
    if (isNil(storedColumnOrder)) {
      setManagedColumnOrder(getDefaultColumnOrder(columns));
      return;
    }

    setManagedColumnOrder(reconcileColumnOrder(storedColumnOrder, columns));
  }, [columnOrder, columnOrderingEnabled, columnOrderStorageKey, columns]);

  const handleColumnOrderChange = useCallback<OnChangeFn<ColumnOrderState>>(
    (updaterOrValue) => {
      setManagedColumnOrder((previous) => {
        const next = typeof updaterOrValue === 'function' ? updaterOrValue(previous) : updaterOrValue;
        const reconciled = reconcileColumnOrder(next, columns);
        if (!isNil(columnOrderStorageKey)) {
          setStoredColumnOrder({ storageKey: columnOrderStorageKey, columnOrder: reconciled });
        }
        onColumnOrderChange?.(reconciled);
        return reconciled;
      });
    },
    [columns, columnOrderStorageKey, onColumnOrderChange],
  );

  const [managedColumnVisibility, setManagedColumnVisibility] = useState<VisibilityState>(() => {
    if (!columnVisibilityEnabled) return {};
    if (!isNil(columnVisibility)) return columnVisibility;

    const defaultColumnVisibility = getDefaultColumnVisibility(columns);
    if (isNil(columnVisibilityStorageKey)) return defaultColumnVisibility;

    const storedColumnVisibility = getStoredColumnVisibility(columnVisibilityStorageKey);
    if (isNil(storedColumnVisibility)) return defaultColumnVisibility;

    return {
      ...defaultColumnVisibility,
      ...storedColumnVisibility,
    };
  });

  // When a controlled columnVisibility prop is provided, keep internal state in sync with it
  useEffect(() => {
    if (columnVisibilityEnabled && !isNil(columnVisibility)) {
      setManagedColumnVisibility(columnVisibility);
    }
  }, [columnVisibility, columnVisibilityEnabled]);

  useEffect(() => {
    if (!columnVisibilityEnabled || !isNil(columnVisibility) || isNil(columnVisibilityStorageKey)) {
      return;
    }

    const storedColumnVisibility = getStoredColumnVisibility(columnVisibilityStorageKey);
    if (isNil(storedColumnVisibility)) return;

    setManagedColumnVisibility({
      ...getDefaultColumnVisibility(columns),
      ...storedColumnVisibility,
    });
  }, [columnVisibility, columnVisibilityEnabled, columnVisibilityStorageKey, columns]);

  const handleColumnVisibilityChange = useCallback<OnChangeFn<VisibilityState>>(
    (updaterOrValue) => {
      setManagedColumnVisibility((previous) => {
        const next = typeof updaterOrValue === 'function' ? updaterOrValue(previous) : updaterOrValue;
        if (!isNil(columnVisibilityStorageKey)) {
          setStoredColumnVisibility({ storageKey: columnVisibilityStorageKey, columnVisibility: next });
        }
        columnVisibilityConfig?.onColumnVisibilityChange?.(next);
        return next;
      });
    },
    [columnVisibilityConfig, columnVisibilityStorageKey],
  );

  const table = useReactTable<TData>({
    data,
    columns: columnsWithSelection,
    getCoreRowModel: getCoreRowModel(),
    ...(bulkActionsEnabled && {
      getRowId: bulkActionsConfig.getRowId,
      enableRowSelection: true,
      onRowSelectionChange: setRowSelection,
    }),
    filterFns,
    // Sorting
    ...(sorting && { getSortedRowModel: getSortedRowModel() }),
    // Sub-rows / hierarchical data
    ...(!isNil(getSubRows) && {
      getExpandedRowModel: getExpandedRowModel(),
      getSubRows,
    }),
    // Only include filtered row model for client-side filtering
    ...(filteringEnabled &&
      !isManualFiltering && {
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
      }),
    // Manual filtering options
    ...(isManualFiltering && { manualFiltering: true }),
    // Only include pagination row model for client-side pagination
    ...(paginationEnabled &&
      !isManualPagination && {
        getPaginationRowModel: getPaginationRowModel(),
      }),
    // Manual pagination options
    ...(isManualPagination && { manualPagination: true }),
    ...(paginationConfig?.rowCount !== undefined && { rowCount: paginationConfig.rowCount }),
    ...(paginationConfig?.pageCount !== undefined && { pageCount: paginationConfig.pageCount }),
    ...(paginationConfig?.autoResetPageIndex !== undefined && {
      autoResetPageIndex: paginationConfig.autoResetPageIndex,
    }),
    // Column resizing
    ...(columnResizing && {
      columnResizeMode: 'onChange',
      onColumnSizingChange: handleColumnSizingChange,
    }),
    // Controlled state
    state: {
      ...(columnFiltersState && { columnFilters: columnFiltersState }),
      ...(paginationState && { pagination: paginationState }),
      ...(columnOrderingEnabled && {
        columnOrder: bulkActionsEnabled
          ? ['select', ...managedColumnOrder.filter((id) => id !== 'select')]
          : managedColumnOrder,
      }),
      ...(!columnOrderingEnabled &&
        !isNil(columnOrder) && {
          columnOrder: bulkActionsEnabled
            ? ['select', ...reconcileColumnOrder(columnOrder, columns).filter((id) => id !== 'select')]
            : reconcileColumnOrder(columnOrder, columns),
        }),
      ...(columnVisibilityEnabled && { columnVisibility: managedColumnVisibility }),
      ...(!columnVisibilityEnabled && !isNil(columnVisibility) && { columnVisibility }),
      ...(columnResizing && { columnSizing }),
      ...(bulkActionsEnabled && { rowSelection }),
    },
    ...(onColumnFiltersChange && { onColumnFiltersChange }),
    ...(onPaginationChange && { onPaginationChange }),
    ...(columnOrderingEnabled && { onColumnOrderChange: handleColumnOrderChange }),
    ...(columnVisibilityEnabled && { onColumnVisibilityChange: handleColumnVisibilityChange }),
    // Uncontrolled initial state
    initialState: {
      ...(!columnFiltersState && filteringEnabled && { columnFilters: filteringConfig?.defaultColumnFilters ?? [] }),
      ...(!paginationState &&
        paginationEnabled && {
          pagination: {
            pageIndex: paginationConfig?.defaultPageIndex ?? 0,
            pageSize: paginationConfig?.defaultPageSize ?? 10,
          },
        }),
      // When sub-rows are provided, expand all by default unless caller opts out
      ...(!isNil(getSubRows) && { expanded: defaultExpanded ? true : {} }),
    },
  });

  useImperativeHandle(tableRef, () => table, [table]);

  const exportState = useExport({
    table,
    exportConfig,
    tableName: name,
  });

  const columnVisibilityState = useColumnVisibility({
    table,
    setColumnVisibility: handleColumnVisibilityChange,
    enabled: columnVisibilityEnabled,
  });

  const updateCell = useCallback(
    (rowId: string, key: keyof TData, value: TData[keyof TData]) => {
      onRowChange?.((prev) => {
        const rowIndex = parseInt(rowId, 10);
        return prev.map((row, index) =>
          index === rowIndex && typeof row === 'object' ? { ...row, [key]: value } : row,
        );
      });
    },
    [onRowChange],
  );

  const { handleFilterChange, handleRemoveFilter, handleRemoveValue, handleClearAll } = useDataTableFilters(table);

  const columnFilters = table.getState().columnFilters;
  const filterableColumns = useMemo(() => getFilterableColumns(table), [columnFilters, columns]);
  const activeFilters = useMemo(() => getActiveFilters(table), [columnFilters, columns]);
  const activeFilterCount = columnFilters.length;

  const contextValue = useMemo(
    () => ({
      updateCell,
      columnResizingEnabled: columnResizing,
      columnOrderingEnabled,
      setMinColumnSize,
      resizeColumn,
      anySelected: selectedIds.length > 0,
    }),
    [updateCell, columnResizing, columnOrderingEnabled, setMinColumnSize, resizeColumn, selectedIds],
  );

  const RootSlot = slots?.root ?? 'div';
  const TableSlot = slots?.table ?? Table;
  const TableHeaderSlot = slots?.header ?? TableHeader;
  const TableBodySlot = slots?.body ?? TableBody;
  const TableFooterSlot = slots?.footer ?? TableFooter;
  const TableCaptionSlot = slots?.caption ?? TableCaption;
  const TableHeaderGroupSlot = slots?.headerGroup ?? DefaultHeaderGroup;
  const TableHeaderCellSlot = slots?.headerCell ?? DefaultHeaderCell;
  const TableRowSlot = slots?.row ?? DefaultRow;
  const TableCellSlot = slots?.cell ?? DefaultCell;
  const TableFooterGroupSlot = slots?.footerGroup ?? DefaultFooterGroup;
  const TableFooterCellSlot = slots?.footerCell ?? DefaultFooterCell;
  const ToolbarBottomSlot = slots?.toolbarBottom ?? DataTableToolbarBottom;
  const PageSizeSlot = slots?.pageSize ?? DataTablePageSize;
  const PaginationSlot = slots?.pagination ?? Pagination;
  const ToolbarTopSlot = slots?.toolbarTop ?? DataTableToolbarTop;
  const ToolbarStartSlot = slots?.toolbarStart ?? null;
  const FiltersSlot = slots?.filters ?? DataTableFilters;
  const ActiveFiltersSlot = slots?.activeFilters ?? DataTableActiveFilters;
  const ExportButtonSlot = slots?.exportButton ?? DataTableExportButton;
  const ExportModalSlot = slots?.exportModal ?? DataTableExportModal;
  const ColumnVisibilitySlot = slots?.columnVisibility ?? DataTableColumnVisibility;

  const pageSize = table.getState().pagination.pageSize;
  const currentPageIndex = table.getState().pagination.pageIndex;
  const totalPages = table.getPageCount();

  const tableRows = table.getRowModel().rows;
  const hasData = tableRows.length > 0;
  const pageRowCount = tableRows.length;
  const pageSelectedCount = tableRows.filter((row) => row.getIsSelected()).length;

  // Opt-in window virtualization (no external dependency): only the rows near
  // the viewport mount, so render cost stays flat as row count grows. The full
  // data list is untouched — selection and bulk actions operate over every row,
  // not just the painted ones. Fixed row height + generous overscan keeps it
  // simple; overscan absorbs minor height variance.
  const virtualizeRowsConfig = typeof virtualizeRowsFeature === 'object' ? virtualizeRowsFeature : undefined;
  const virtualizeRowsEnabled = virtualizeRowsFeature !== false;
  const rowHeight = virtualizeRowsConfig?.rowHeight ?? 56;
  const overscan = virtualizeRowsConfig?.overscan ?? 12;
  const tableContentRef = useRef<HTMLDivElement>(null);
  const [virtualViewport, setVirtualViewport] = useState({ scrollTop: 0, height: 0 });

  useEffect(() => {
    if (!virtualizeRowsEnabled) return undefined;
    let frame = 0;
    const measure = () => {
      frame = 0;
      setVirtualViewport({ scrollTop: window.scrollY, height: window.innerHeight });
    };
    const onScrollOrResize = () => {
      if (frame === 0) frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, [virtualizeRowsEnabled]);

  const rowCount = tableRows.length;
  const scrollMargin = tableContentRef.current?.offsetTop ?? 0;
  const virtualStartIndex = virtualizeRowsEnabled
    ? Math.max(0, Math.floor((virtualViewport.scrollTop - scrollMargin) / rowHeight) - overscan)
    : 0;
  const virtualEndIndex = virtualizeRowsEnabled
    ? Math.min(
        rowCount,
        Math.ceil((virtualViewport.scrollTop - scrollMargin + virtualViewport.height) / rowHeight) + overscan,
      )
    : rowCount;
  const virtualPaddingTop = virtualizeRowsEnabled ? virtualStartIndex * rowHeight : 0;
  const virtualPaddingBottom = virtualizeRowsEnabled ? Math.max(0, (rowCount - virtualEndIndex) * rowHeight) : 0;

  const renderBodyRow = (row: (typeof tableRows)[number], rowIndex: number) => {
    const isSelected = row.getIsSelected();
    const selectable = checkRowSelectable(row.original);
    return (
      <TableRowSlot
        row={row}
        index={rowIndex}
        key={row.id}
        data-state={isSelected && 'selected'}
        className={cn(
          bulkActionsEnabled && BULK_SELECT_ROW_GROUP,
          bulkActionsEnabled && isSelected && '!bg-table-hover',
          bulkActionsEnabled && rowClickTogglesEnabled && (selectable || isSelected) && 'cursor-pointer',
          bulkActionsEnabled && !selectable && !isSelected && 'opacity-50',
          resolveClassName(classNames?.row, { row, index: rowIndex }),
        )}
        {...(() => {
          const resolved = resolveSlotProps(slotProps?.row, { row, index: rowIndex });
          if (!bulkActionsEnabled || !rowClickTogglesEnabled) return resolved;
          const callerOnClick = resolved.onClick as React.MouseEventHandler | undefined;
          return {
            ...resolved,
            onClick: (e: React.MouseEvent<HTMLTableRowElement>) => {
              const target = e.target as HTMLElement;
              if (target.closest('[data-bulk-select-cell]') !== null) {
                return;
              }
              if (isSelected || selectable) row.toggleSelected();
              callerOnClick?.(e);
            },
          };
        })()}
      >
        {row.getVisibleCells().map((cell, cellIndex) => (
          <TableCellSlot
            cell={cell}
            rowIndex={rowIndex}
            cellIndex={cellIndex}
            key={cell.id}
            className={resolveClassName(classNames?.cell, { cell, rowIndex, cellIndex })}
            {...resolveSlotProps(slotProps?.cell, { cell, rowIndex, cellIndex })}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCellSlot>
        ))}
      </TableRowSlot>
    );
  };

  const renderBodyContent = () => {
    // Span visible columns only — a colSpan beyond the header's column count adds
    // phantom columns that absorb the table's leftover width.
    const visibleColumnCount = Math.max(1, table.getVisibleLeafColumns().length);
    if (!tableRows.length) {
      return (
        <TableRow>
          <TableCell colSpan={visibleColumnCount} className="h-24 text-center">
            {emptyState ?? 'No results.'}
          </TableCell>
        </TableRow>
      );
    }
    if (!virtualizeRowsEnabled) {
      return tableRows.map((row, rowIndex) => renderBodyRow(row, rowIndex));
    }
    return (
      <>
        {virtualPaddingTop > 0 && (
          <tr aria-hidden>
            <td colSpan={visibleColumnCount} style={{ height: virtualPaddingTop, padding: 0, border: 0 }} />
          </tr>
        )}
        {tableRows
          .slice(virtualStartIndex, virtualEndIndex)
          .map((row, offset) => renderBodyRow(row, virtualStartIndex + offset))}
        {virtualPaddingBottom > 0 && (
          <tr aria-hidden>
            <td colSpan={visibleColumnCount} style={{ height: virtualPaddingBottom, padding: 0, border: 0 }} />
          </tr>
        )}
      </>
    );
  };

  const showToolbarTop =
    filteringEnabled ||
    exportState.exportEnabled ||
    columnVisibilityState.columnVisibilityEnabled ||
    !!ToolbarStartSlot;
  const orderableVisibleColumnIds = table
    .getVisibleLeafColumns()
    .filter((column) => isColumnOrderable(column.columnDef.meta))
    .map((column) => column.id);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleColumnDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!columnOrderingEnabled || isNil(over) || active.id === over.id) return;
      const activeId = String(active.id);
      const overId = String(over.id);
      const nextOrder = reorderColumnsWithinSubset({
        columnOrder: managedColumnOrder,
        movableColumnIds: orderableVisibleColumnIds,
        activeId,
        overId,
      });
      handleColumnOrderChange(nextOrder);
    },
    [columnOrderingEnabled, handleColumnOrderChange, managedColumnOrder, orderableVisibleColumnIds],
  );
  const headerContent = (
    <TableHeaderSlot
      className={cn(stickyHeaderEnabled && 'sticky z-10', classNames?.header)}
      {...slotProps?.header}
      style={{
        ...(stickyHeaderEnabled && { top: toCssLength(stickyHeaderConfig.top ?? 0) }),
        ...slotProps?.header?.style,
      }}
    >
      {table.getHeaderGroups().map((headerGroup, headerGroupIndex) => (
        <TableHeaderGroupSlot
          headerGroup={headerGroup}
          index={headerGroupIndex}
          key={headerGroup.id}
          className={cn(
            bulkActionsEnabled && BULK_SELECT_ROW_GROUP,
            resolveClassName(classNames?.headerGroup, { headerGroup, index: headerGroupIndex }),
          )}
          {...resolveSlotProps(slotProps?.headerGroup, { headerGroup, index: headerGroupIndex })}
        >
          {headerGroup.headers.map((header, headerIndex) => {
            return (
              <TableHeaderCellSlot
                header={header}
                headerGroupIndex={headerGroupIndex}
                headerIndex={headerIndex}
                key={header.id}
                className={resolveClassName(classNames?.headerCell, {
                  header,
                  headerGroupIndex,
                  headerIndex,
                })}
                {...resolveSlotProps(slotProps?.headerCell, { header, headerGroupIndex, headerIndex })}
              >
                {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHeaderCellSlot>
            );
          })}
        </TableHeaderGroupSlot>
      ))}
    </TableHeaderSlot>
  );

  // DndContext renders an aria-live announcer <div>; keep it outside the
  // <table> so it isn't an invalid child. SortableContext (no DOM) stays
  // around the header cells below.
  const withColumnReorder = (children: ReactNode) =>
    columnOrderingEnabled ? (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleColumnDragEnd}>
        {children}
      </DndContext>
    ) : (
      children
    );

  return (
    <DataTableContext.Provider value={contextValue}>
      <RootSlot
        className={cn(
          'rounded-md border border-light',
          // See `tableContentOverflowClass`: clip, not hidden, so the card still clips
          // its corners without capturing the page-pinned header.
          stickyHeaderEnabled && !stickyHeaderScrollsInternally ? 'overflow-clip' : 'overflow-hidden',
          classNames?.root,
        )}
        {...slotProps?.root}
      >
        {showToolbarTop && (
          <ToolbarTopSlot className={classNames?.toolbarTop}>
            <div className="flex items-center justify-between gap-2">
              {ToolbarStartSlot && <ToolbarStartSlot />}
              {filteringEnabled && (
                <FiltersSlot
                  columns={filterableColumns}
                  activeCount={activeFilterCount}
                  onFilterChange={handleFilterChange}
                  className={classNames?.filters}
                />
              )}
              {(columnVisibilityState.columnVisibilityEnabled || exportState.exportEnabled) && (
                <div className="ml-auto flex items-center gap-2">
                  {columnVisibilityState.columnVisibilityEnabled && (
                    <ColumnVisibilitySlot
                      open={columnVisibilityState.open}
                      onOpenChange={columnVisibilityState.handleOpenChange}
                      columns={columnVisibilityState.columns}
                      pendingColumnIds={columnVisibilityState.pendingColumnIds}
                      disabled={!hasData}
                      toggleColumn={columnVisibilityState.toggleColumn}
                      showAll={columnVisibilityState.showAll}
                      hideAll={columnVisibilityState.hideAll}
                      apply={columnVisibilityState.apply}
                      reset={columnVisibilityState.reset}
                      className={resolveClassName(classNames?.columnVisibility, {
                        isOpen: columnVisibilityState.open,
                        columns: columnVisibilityState.columns,
                        pendingColumnIds: columnVisibilityState.pendingColumnIds,
                      })}
                      {...resolveSlotProps(slotProps?.columnVisibility, {
                        isOpen: columnVisibilityState.open,
                        columns: columnVisibilityState.columns,
                        pendingColumnIds: columnVisibilityState.pendingColumnIds,
                      })}
                    />
                  )}
                  {exportState.exportEnabled && (
                    <ExportButtonSlot
                      disabled={!hasData}
                      onClick={() => exportState.setModalOpen(true)}
                      className={resolveClassName(classNames?.exportButton, { hasData })}
                      {...resolveSlotProps(slotProps?.exportButton, { hasData })}
                    />
                  )}
                </div>
              )}
            </div>
            {filteringEnabled && (
              <ActiveFiltersSlot
                filters={activeFilters}
                onRemoveFilter={handleRemoveFilter}
                onRemoveValue={handleRemoveValue}
                onClearAll={handleClearAll}
                className={classNames?.activeFilters}
              />
            )}
          </ToolbarTopSlot>
        )}
        <div
          ref={tableContentRef}
          data-slot="table-content"
          data-sticky-header={stickyHeaderMode}
          className={cn(
            'border-x border-b border-light -mx-px',
            // With a sticky header this is the one scroll container: it either scrolls the
            // body under a pinned header, or gets out of the way so the page can scroll.
            tableContentOverflowClass,
            // Pagination supplies the card's bottom edge; rounding here would curve the divider above it.
            !paginationEnabled && 'rounded-b-md',
            classNames?.tableContent,
          )}
          style={tableContentStyle}
        >
          {withColumnReorder(
            <TableSlot
              size={size}
              borderless={borderless}
              className={classNames?.table}
              {...slotProps?.table}
              // The kit's Table wraps every table in its own `overflow-x-auto` div. That div
              // is a scrollport in both axes, so it — not the page, and not tableContent —
              // is what a sticky `<thead>` resolves against, and it never scrolls vertically.
              // Neutralising it is what makes the header actually pin; verified in Chromium.
              containerClassName={cn(stickyHeaderEnabled && 'overflow-x-visible', slotProps?.table?.containerClassName)}
              style={{
                ...slotProps?.table?.style,
                ...(!isNil(minTableWidth) && {
                  minWidth: typeof minTableWidth === 'number' ? `${minTableWidth}px` : minTableWidth,
                }),
                ...(columnResizing && { tableLayout: 'fixed' }),
              }}
            >
              {caption && (
                <TableCaptionSlot className={classNames?.caption} {...slotProps?.caption}>
                  {caption}
                </TableCaptionSlot>
              )}
              {header &&
                (columnOrderingEnabled ? (
                  <SortableContext items={orderableVisibleColumnIds} strategy={horizontalListSortingStrategy}>
                    {headerContent}
                  </SortableContext>
                ) : (
                  headerContent
                ))}
              <TableBodySlot className={classNames?.body} {...slotProps?.body}>
                {renderBodyContent()}
              </TableBodySlot>
              {footer && (
                <TableFooterSlot className={classNames?.footer} {...slotProps?.footer}>
                  {table.getFooterGroups().map((footerGroup, footerGroupIndex) => (
                    <TableFooterGroupSlot
                      footerGroup={footerGroup}
                      index={footerGroupIndex}
                      key={footerGroup.id}
                      className={resolveClassName(classNames?.footerGroup, { footerGroup, index: footerGroupIndex })}
                      {...resolveSlotProps(slotProps?.footerGroup, { footerGroup, index: footerGroupIndex })}
                    >
                      {footerGroup.headers.map((footer, footerIndex) => (
                        <TableFooterCellSlot
                          footer={footer}
                          footerGroupIndex={footerGroupIndex}
                          footerIndex={footerIndex}
                          key={footer.id}
                          className={resolveClassName(classNames?.footerCell, {
                            footer,
                            footerGroupIndex,
                            footerIndex,
                          })}
                          {...resolveSlotProps(slotProps?.footerCell, { footer, footerGroupIndex, footerIndex })}
                        >
                          {footer.isPlaceholder
                            ? null
                            : flexRender(footer.column.columnDef.footer, footer.getContext())}
                        </TableFooterCellSlot>
                      ))}
                    </TableFooterGroupSlot>
                  ))}
                </TableFooterSlot>
              )}
            </TableSlot>,
          )}
        </div>
        {paginationEnabled && (
          <ToolbarBottomSlot className={classNames?.toolbarBottom}>
            <PageSizeSlot
              pageSize={pageSize}
              onPageSizeChange={(pageSize) => {
                table.setPageSize(pageSize);
              }}
              pageSizeOptions={paginationConfig?.pageSizeOptions}
              className={resolveClassName(classNames?.pageSize, { pageSize })}
              {...resolveSlotProps(slotProps?.pageSize, { pageSize })}
            />
            <PaginationSlot
              currentPageIndex={currentPageIndex}
              totalPages={totalPages}
              onPageChange={(pageIndex) => {
                table.setPageIndex(pageIndex);
              }}
              className={cn('mx-0 w-auto', resolveClassName(classNames?.pagination, { currentPageIndex, totalPages }))}
              {...resolveSlotProps(slotProps?.pagination, {
                currentPageIndex,
                totalPages,
              })}
            />
          </ToolbarBottomSlot>
        )}
        {exportState.exportEnabled && (
          <ExportModalSlot
            open={exportState.modalOpen}
            onOpenChange={exportState.handleModalOpenChange}
            {...exportState}
            className={resolveClassName(classNames?.exportModal, {
              isOpen: exportState.modalOpen,
              columns: exportState.columns,
              selectedColumnIds: exportState.selectedColumnIds,
              selectedFormat: exportState.selectedFormat,
              state: exportState.state,
            })}
            {...resolveSlotProps(slotProps?.exportModal, {
              isOpen: exportState.modalOpen,
              columns: exportState.columns,
              selectedColumnIds: exportState.selectedColumnIds,
              selectedFormat: exportState.selectedFormat,
              state: exportState.state,
            })}
          />
        )}
      </RootSlot>
      {bulkActionsEnabled && selectedIds.length > 0 && (
        <SelectionBar
          selectedIds={selectedIds}
          pageSelectedCount={pageSelectedCount}
          pageRowCount={pageRowCount}
          actions={bulkActionsConfig.actions ?? []}
          onCancel={() => setRowSelection({})}
        />
      )}
    </DataTableContext.Provider>
  );
}
